import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { filter, tap } from "rxjs/operators";
import { ListSelectable } from "src/model/class/general classes/list-selectable";
import { Notific } from "src/model/class/general classes/notific";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { InnerComponentDataError } from "src/model/enum/enumInnerComponentDataError";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { AlertService } from "src/service/alert.service";
import { EventService } from "src/service/event.service";
import { NetworkService } from "src/service/network.service";
import { NotificationService } from "src/service/notification.service";
import { listNotification } from "src/state/selectors/notifications.selectors";
import { serverSettings } from "src/state/selectors/server-settings-selector";

@Component({
    selector: "app-list-notifications-selectable",
    templateUrl: "./list-notifications.component.html",
    styleUrls: ["./list-notifications.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
/**
 *
 *
 *  Generic list notification selectable.
 *  NB: In onClickNotification event i don't store current notification selected beacuse it's not important at this point.
 *  I use OnAssignDisassignNotificationToGenericObjectEvent to propagate item list selection/unselection
 *
 *
 */
export class ListNotificationsSelectableComponent
  implements OnInit, OnDestroy, iTranslation {
  //#region Subcriptions to "_store.select(...)" Observables
  private _selectServerSettingsUserSub: Subscription = null;
  private _selectListNotificationUserSub: Subscription = null;

  private _networkServiceSubscription: Subscription = null;
  //#endregion

  private _translateSubscription: Subscription = null;

  /**Inner list of "selectable" notifications; when it is changed by user interaction, it is emitted to the father-component*/
  private _listNotificationSelected: Array<Notific> = new Array<Notific>();

  //#region  Translated strings
  private _lblNoNotificationFound: string = "";
  //#endregion

  /**Notification types list */
  private _notifications: Array<Notific> = null;

  private _isForcingReloading: boolean = false;

  public isNotificationLoaded: boolean = false;
  public lblSearchNotification: string = "";

  /**Publicly visible copy of the Notification types list; it is dropped and re-initialised to "reset" the list in the html template when needed.*/
  public notificationsIcons: Array<Notific> = null;

  public searchText: string = "";
  public allowEdit = true;
  public noContentText = "";
  public showErrorContent: boolean = false;

  public toolBarColor: string = "primary";

  public connectionType = ConnectionType;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;

  @Input("IdToSelect") idToSelect: Array<number>;
  @Input("showSearchBar") showSearchBar: boolean;
  @Input("showWithAudio") showWithAudio: boolean;
  @Input("showWithBalloon") showWithBalloon: boolean;
  @Input("showWithMail") showWithMail: boolean;
  @Input("showWithPageJump") showWithPageJump: boolean;
  @Input("showWithPopup") showWithPopup: boolean;
  @Input("showWithSMS") showWithSMS: boolean;
  /**
   * If component is being displaying again after a previous initilisation resulted in a data-fetching error, this flag is TRUE;
   * this will result in triggering of the associated service "forceReload" method. Otherwise, it is set to FALSE.
   */
  @Input("isForcingReloading") isForcingReloading: boolean;

  @Output() onListNotificationSelectUpdated: EventEmitter<Array<Notific>> =
    new EventEmitter<Array<Notific>>();

  /**
   * This emttter is used to signal the "father" component/page that a general error has been launched inside the list
   * (USUALLY, this means an error occurred in fetching the displayed data, whhich means an error occurred in the APIs calls
   * used to populate the store).
   * NOTE: This solution is NECESSARY because the "Error-content.component" can NOT be used directly inside this component,
   * due to circular dependency.
   */
  @Output() errorInNotificationList =
    new EventEmitter<InnerComponentDataError>();

  constructor(
    private _notificService: NotificationService,
    private _eventService: EventService,
    private _alertService: AlertService,
    private _translateService: TranslateService,
    private _networkService: NetworkService,
    private _store: Store,
    private _dc: ChangeDetectorRef
  ) { }

  /**
   * Subscribe to the public Observable of "NetworkService", which emits each time the connection change of type/state. A local property is changed to
   * reflect the new value.
   */
  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }

  async ngOnInit() {
    this._isForcingReloading = this.isForcingReloading;

    this.handleFallbackMissingInput();
    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.subscribeNetworkServiceData();

    this._notificService.Init();

    await this.loadNotifications();

    if (this._isForcingReloading) {
      this.forceReloadData();
    }

    /** GF: enable edit only if the user can use it */
    this._selectServerSettingsUserSub = this._store
      .select(serverSettings)
      .pipe(filter((setting) => setting !== null))
      .subscribe((settingsValue) => {
        if (!settingsValue.ShowSchedule_Editing) {
          this.allowEdit = false;
        }
      });
  }

  ngOnChanges() {
    this.idToSelect = this.idToSelect ??= [];
  }

  ngOnDestroy() {
    this.unsubscribeStoreSelect();
    this._translateSubscription.unsubscribe();
    this._networkServiceSubscription.unsubscribe();
  }

  /**
   *
   * @param idNotification: id notification to check selection
   *
   * {@link setNotificationChecked} check if notification id is need to check on initialization of entire component list
   *
   */
  public setNotificationChecked(idNotification: number): boolean {
    return this.idToSelect.includes(idNotification);
  }

  /**
   * This method reset the inner list containing the currently selected notification.
   */
  public resetNotificationsSelection(): void {
    this._listNotificationSelected = new Array<Notific>();
    this.idToSelect = [];
    this.notificationsIcons = [];
    this.refreshDOM();
    this.notificationsIcons = this._notifications;

    this.onListNotificationSelectUpdated.emit(this._listNotificationSelected);
  }

  /**
   *
   * @param idNotification: id of clicked notification
   *
   * @param notificationElement : refrence of specific IonToggle
   *
   * {@link onClickNotification} is fired when ion toggle is clicked: when it happens,
   * it propagate OnAssignDisassignNotificationToGenericObjectEvent event passing current notification id and it's selection
   *
   */
  public onClickNotification(idNotification: number, notificationElement: any) {
    const itemNotification: ListSelectable = new ListSelectable();

    itemNotification.id = idNotification;
    itemNotification.checked = !notificationElement.checked;

    this.handleListNotificationSelectedByClickedItem(itemNotification);

    this._eventService.OnAssignDisassignNotificationToGenericObjectEvent.next(
      itemNotification
    );

    this.onListNotificationSelectUpdated.emit(this._listNotificationSelected);
  }

  public onSearch(searchElement: any) {
    this.searchText = searchElement.value;
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get(["dictionary.searchNotification", "dictionary.noNotificationFound"])
      .subscribe((translation) => {
        this.lblSearchNotification =
          translation["dictionary.searchNotification"];
        this._lblNoNotificationFound =
          translation["dictionary.noNotificationFound"];

        this.noContentText = this._lblNoNotificationFound;
      });
  }

  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * Force reload data after "Something went wrong" button refresh click
   */
  public forceReloadData() {
    this._notificService.reloadServiceData();
    this._isForcingReloading = false;
  }

  /**
   * This method forces Angular to check if there has been a recent chnage in page-backing data.
   *
   * ATTENTION: This should be used ONLY when a immediate data-binding-reaction is needed (EX: when DOM should change just before the page is left.)
   */
  private refreshDOM(): void {
    this._dc.detectChanges();
  }

  private async loadNotifications(): Promise<void> {
    this._selectListNotificationUserSub = this._store
      .select(listNotification)
      .pipe(
        filter(
          (listNotificationObject: GenericStoreApiData) =>
            !this._isForcingReloading
        ),
        filter(
          (listNotificationObject: GenericStoreApiData) =>
            listNotificationObject !== null
        ),
        tap(
          (listNotificationObject: GenericStoreApiData) =>
            (this.isNotificationLoaded = listNotificationObject.loaded)
        ),
        filter(
          (listNotificationObject: GenericStoreApiData) =>
            listNotificationObject.loaded
        )
      )
      .subscribe(async (listNotificationObject: GenericStoreApiData) => {
        await this._alertService.clearAlertOk();

        this.showErrorContent = listNotificationObject.errorGeneric;

        if (listNotificationObject.errorGeneric) {
          this.handleApiResponseError();
        } else {
          const listNotificationData: Array<Notific> =
            listNotificationObject.data?.data || [];
          this._notifications =
            this.filterByInputProperty(listNotificationData);
          this.notificationsIcons = this._notifications;
        }
      });
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectServerSettingsUserSub) {
      this._selectServerSettingsUserSub.unsubscribe();
    }

    if (this._selectListNotificationUserSub) {
      this._selectListNotificationUserSub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Show something went wrong
   */
  private async handleApiResponseError(): Promise<void> {
    //this.showErrorContent = true;
    this.errorInNotificationList.emit(
      InnerComponentDataError.LIST_NOTIFICATIONS
    );
  }

  /**
   * Handle fallback for missing input parameter
   */
  private handleFallbackMissingInput(): void {
    this.idToSelect = this.idToSelect ?? [];
    this.showSearchBar = this.showSearchBar ?? true;
    this.showWithAudio = this.showWithAudio ??= true;
    this.showWithBalloon = this.showWithBalloon ??= true;
    this.showWithMail = this.showWithMail ??= true;
    this.showWithPageJump = this.showWithPageJump ??= true;
    this.showWithPopup = this.showWithPopup ??= true;
    this.showWithSMS = this.showWithSMS ??= true;
  }

  /**
   * Apply list filter based on input component parameter
   *
   * @param listNotificaton List notification to filter
   * @returns Filtered list
   */
  private filterByInputProperty(
    listNotificaton: Array<Notific>
  ): Array<Notific> {
    if (!this.showWithAudio) {
      listNotificaton = listNotificaton.filter(
        (notification: Notific) => !notification.withAudio
      );
    }

    if (!this.showWithBalloon) {
      listNotificaton = listNotificaton.filter(
        (notification: Notific) => !notification.withBalloon
      );
    }

    if (!this.showWithMail) {
      listNotificaton = listNotificaton.filter(
        (notification: Notific) => !notification.withMail
      );
    }

    if (!this.showWithPageJump) {
      listNotificaton = listNotificaton.filter(
        (notification: Notific) => !notification.withPageJump
      );
    }

    if (!this.showWithPopup) {
      listNotificaton = listNotificaton.filter(
        (notification: Notific) => !notification.withPopup
      );
    }

    if (!this.showWithSMS) {
      listNotificaton = listNotificaton.filter(
        (notification: Notific) => !notification.withPopup
      );
    }

    return listNotificaton;
  }

  /**
   * Update innser list selectable notification by clicked notiffication.
   * If notification is selected it append to selected list notification.
   * Otherwiser is remove from list
   */
  private handleListNotificationSelectedByClickedItem(
    notification: ListSelectable
  ): void {
    if (notification.checked) {
      const foundedNotification: Notific = this._notifications.find(
        (notific: Notific) => notific.id === notification.id
      );

      this._listNotificationSelected.push(foundedNotification);
    } else {
      this._listNotificationSelected = this._listNotificationSelected.filter(
        (notif: Notific) => notif.id !== notification.id
      );
    }
  }
}
